import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Check, RefreshCw, Clock, CalendarDays } from "lucide-react";
import AllotmentsDrawer from "./AllotmentsDrawer";
import { CACHE_KEYS } from "../utils/cacheManager";

// Constants for platform colors
const PLATFORM_COLORS = {
  'airbnb': '#f87171',   // red-400 — softer, fits dark palette
  'vrbo': '#818cf8',     // indigo-400 — matches violet theme
  'homeaway': '#818cf8',
  'booking': '#94a3b8',  // slate-400 — readable on dark bg
  'default': '#71717a'
};

// Date utility functions
const generateCalendarDates = () => {
  const today = new Date();
  const dates = [];

  // Find the Monday before last Monday
  const lastMonday = new Date(today);
  lastMonday.setDate(today.getDate() - (today.getDay() + 6) % 7);
  lastMonday.setDate(lastMonday.getDate() - 7);

  // Find the Sunday 4 weeks after next Monday
  const endDate = new Date(today);
  endDate.setDate(today.getDate() + (8 - today.getDay()) % 7);
  endDate.setDate(endDate.getDate() + 28);
  endDate.setDate(endDate.getDate() - endDate.getDay());

  for (let date = new Date(lastMonday); date <= endDate; date.setDate(date.getDate() + 1)) {
    dates.push(new Date(date));
  }
  return dates;
};

const formatDate = (date) => {
  return new Intl.DateTimeFormat("en-US", {
    weekday: "short",
    month: "numeric",
    day: "numeric",
  }).format(date);
};

const isToday = (date) => {
  return date.toDateString() === new Date().toDateString();
};

const isSunday = (date) => {
  return date.getDay() === 0;
};

const getCircleColor = (platform) => {
  return PLATFORM_COLORS[platform?.toLowerCase()] || PLATFORM_COLORS.default;
};

// localStorage cache for the lightweight slots data
const CACHE_KEY = 'calendar_slots_cache';
const CACHE_TTL = 2 * 60 * 60 * 1000; // 2 hours

const getCache = () => {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) return null;
    const { data, timestamp } = JSON.parse(cached);
    if (Date.now() - timestamp < CACHE_TTL) {
      return data;
    }
    return null;
  } catch (e) {
    return null;
  }
};

const setCache = (data) => {
  try {
    // Clear old calendar caches first to free space
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.startsWith('calendar_') || key.startsWith('calendar_slots'))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));

    localStorage.setItem(CACHE_KEY, JSON.stringify({ data, timestamp: Date.now() }));
  } catch (e) {
    console.warn('Failed to cache calendar data:', e);
    // If still failing, clear more aggressively
    try {
      localStorage.removeItem(CACHE_KEY);
      localStorage.removeItem('calendar_reservations_cache');
      localStorage.removeItem('calendar_checkmarks_cache');
      localStorage.removeItem('calendar_last_sync_cache');
    } catch (e2) {
      // Give up on caching
    }
  }
};

const Calendar = ({ properties = [], cleaners = [] }) => {
  // Calendar slots - lightweight data from new endpoint
  const [slots, setSlots] = useState([]);
  const [checkedStates, setCheckedStates] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isManualSyncing, setIsManualSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [tempReassignments, setTempReassignments] = useState([]);
  const [isAllotmentsOpen, setIsAllotmentsOpen] = useState(false);
  const { cleanerId } = useParams();
  const scrollRef = useRef(null);
  const hasAutoScrolled = useRef(false);
  const hasLoadedFromCache = useRef(false);

  // Memoized calendar dates
  const calendarDates = useMemo(() => generateCalendarDates(), []);

  // Memoized filtered properties with day assignments + temp reassignment overlay
  const { filteredProperties, isInvalidLink } = useMemo(() => {
    if (cleanerId) {
      // Never fall through to the all-properties view on a cleaner link —
      // if cleaner data hasn't loaded yet, show an empty grid instead of
      // the full company schedule
      if (cleaners.length === 0) {
        return { filteredProperties: [], isInvalidLink: false };
      }

      let decodedName;
      try {
        decodedName = atob(cleanerId);
      } catch (e) {
        return { filteredProperties: [], isInvalidLink: true };
      }
      const cleaner = cleaners.find(
        (c) => c.name.replace(/\s+/g, "-").toLowerCase() === decodedName
      );

      if (cleaner) {
        const cleanerIdStr = cleaner._id?.toString ? cleaner._id.toString() : cleaner._id;

        // Build base properties from permanent assignments
        let displayProps = (cleaner.assignedProperties || []).map(assignment => {
          const propId = assignment.propertyId?._id || assignment.propertyId;
          const property = properties.find(
            p => p._id?.toString() === propId?.toString()
          );
          if (!property) return null;
          return {
            ...property,
            assignedDays: assignment.assignedDays,
            reassignedAwayRanges: [], // populated below
          };
        }).filter(Boolean);

        // Track properties added for target-cleaner entries
        const existingPropIds = new Set(displayProps.map(p => p._id?.toString()));

        for (const tr of tempReassignments) {
          const trStart = new Date(tr.startDate);
          const trEnd = new Date(tr.endDate);
          // Skip fully expired (calendar-slots already filters >7 days old, but guard here too)
          const trPropIds = (tr.propertyIds || []).map(id => id?.toString ? id.toString() : id);
          const srcId = tr.sourceCleanerId?.toString ? tr.sourceCleanerId.toString() : tr.sourceCleanerId;
          const tgtId = tr.targetCleanerId?.toString ? tr.targetCleanerId.toString() : tr.targetCleanerId;

          if (srcId === cleanerIdStr) {
            // Source cleaner: add a date range to the affected properties
            for (const prop of displayProps) {
              if (trPropIds.includes(prop._id?.toString())) {
                prop.reassignedAwayRanges.push({ startDate: trStart, endDate: trEnd });
              }
            }
          }

          if (tgtId === cleanerIdStr) {
            // Target cleaner: add reassigned-in properties (only those not already permanent)
            trPropIds.forEach((id, idx) => {
              if (existingPropIds.has(id)) return;
              const property = properties.find(p => p._id?.toString() === id);
              if (!property) return;
              displayProps.push({
                ...property,
                assignedDays: null,
                reassignedAwayRanges: [],
                isTemporarilyReassignedTo: true,
                tempStartDate: trStart,
                tempEndDate: trEnd,
              });
              existingPropIds.add(id);
            });
          }
        }

        return { filteredProperties: displayProps, isInvalidLink: false };
      }
      return { filteredProperties: [], isInvalidLink: true };
    }
    return {
      filteredProperties: properties.filter(p => !p.hidden).map(p => ({ ...p, assignedDays: null, reassignedAwayRanges: [] })),
      isInvalidLink: false,
    };
  }, [cleanerId, cleaners, properties, tempReassignments]);

  // Load calendar slots from the new lightweight endpoint
  const loadCalendarSlots = useCallback(async () => {
    try {
      console.log('[Calendar] Fetching lightweight slots...');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/reservations/calendar-slots`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
        },
      });

      if (!response.ok) throw new Error('Failed to load calendar slots');

      const data = await response.json();

      console.log(`[Calendar] Loaded ${data.slots?.length || 0} slots`);

      setSlots(data.slots || []);
      setCheckedStates(data.checkedStates || {});
      setTempReassignments(data.tempReassignments || []);
      if (data.lastSyncTime) {
        setLastSyncTime(new Date(data.lastSyncTime));
      }

      // Cache the lightweight data (it's small enough now!)
      setCache(data);

      return data;
    } catch (error) {
      console.error("Error loading calendar slots:", error);
      return null;
    }
  }, []);

  // Manual sync - calls big-sync to update reservations, payouts, and reports
  const handleManualSync = useCallback(async () => {
    if (cleanerId) return;

    try {
      setIsManualSyncing(true);
      console.log('[Calendar] Starting big-sync...');

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/reservations/big-sync`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        console.log('[Calendar] Big-sync complete:', result);

        await new Promise(resolve => setTimeout(resolve, 1000));
        await loadCalendarSlots();

        // Clear payout/report caches so they reload fresh data
        // (these are the real cacheManager keys — the old 'payoutSlots'/
        // 'reportSlots' strings never matched anything)
        localStorage.removeItem(CACHE_KEYS.PAYOUT_SLOTS);
        localStorage.removeItem(CACHE_KEYS.REPORT_SLOTS);
      } else {
        console.error('[Calendar] Big-sync failed:', response.status);
      }
    } catch (error) {
      console.error("Manual sync error:", error);
    } finally {
      setIsManualSyncing(false);
    }
  }, [cleanerId, loadCalendarSlots]);

  // Initial load: Use cache if available, then fetch fresh data
  useEffect(() => {
    if (hasLoadedFromCache.current) {
      return;
    }

    const initializeCalendar = async () => {
      // Try cache first for instant display
      const cached = getCache();
      if (cached) {
        console.log('[Calendar] Using cached slots');
        setSlots(cached.slots || []);
        setCheckedStates(cached.checkedStates || {});
        setTempReassignments(cached.tempReassignments || []);
        if (cached.lastSyncTime) {
          setLastSyncTime(new Date(cached.lastSyncTime));
        }
        setIsLoading(false);
        hasLoadedFromCache.current = true;
        return;
      }

      // No cache - fetch from network
      await loadCalendarSlots();
      setIsLoading(false);
      hasLoadedFromCache.current = true;
    };

    initializeCalendar();
  }, [loadCalendarSlots]);

  // Auto-scroll to current week
  useEffect(() => {
    if (scrollRef.current && calendarDates.length > 0 && !hasAutoScrolled.current && !isLoading) {
      const rowHeight = 53;
      const scrollToRow = 7;
      scrollRef.current.scrollTo({
        top: rowHeight * scrollToRow,
        behavior: 'smooth'
      });
      hasAutoScrolled.current = true;
    }
  }, [calendarDates.length, isLoading]);

  // Toggle checkmark - create/delete in database
  const handleCheckmarkToggle = useCallback(async (reservationId) => {
    const isChecked = checkedStates[reservationId];

    // Set/unset one reservation without touching other in-flight toggles
    const withToggle = (states, checked) => {
      const next = { ...states };
      if (checked) {
        next[reservationId] = true;
      } else {
        delete next[reservationId];
      }
      return next;
    };

    const syncCache = (checked) => {
      const cached = getCache();
      if (cached) {
        setCache({ ...cached, checkedStates: withToggle(cached.checkedStates || {}, checked) });
      }
    };

    // Optimistic update
    setCheckedStates(prev => withToggle(prev, !isChecked));
    syncCache(!isChecked);

    try {
      const response = await fetch(
        isChecked
          ? `${process.env.REACT_APP_API_URL}/api/calendar-status/${reservationId}`
          : `${process.env.REACT_APP_API_URL}/api/calendar-status`,
        {
          method: isChecked ? 'DELETE' : 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
          },
          body: isChecked ? null : JSON.stringify({ reservationId }),
        }
      );

      if (!response.ok) throw new Error('Failed to update');

    } catch (error) {
      console.error('Error toggling checkmark:', error);
      // Revert only this reservation, in state and cache
      setCheckedStates(prev => withToggle(prev, isChecked));
      syncCache(isChecked);
    }
  }, [checkedStates]);

  // Format sync time
  const formatSyncTime = useCallback(() => {
    if (!lastSyncTime) return 'Never synced';

    return lastSyncTime.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    }).replace(/:\d{2}$/, ':00');
  }, [lastSyncTime]);

  // Memoized table rows - now uses lightweight slots instead of full reservations
  const tableRows = useMemo(() => {
    return calendarDates.map((date, idx) => {
      const dateString = date.toDateString();

      const cells = filteredProperties.map((property, propertyIndex) => {
        const dayOfWeek = date.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();

        // For temp-reassigned-to properties, only show during the reassignment window
        const isInTempWindow = !property.isTemporarilyReassignedTo || (
          date >= property.tempStartDate && date <= property.tempEndDate
        );

        const isAssignedDay = isInTempWindow && (!property.assignedDays || property.assignedDays.includes(dayOfWeek));

        // Source cleaner: check if THIS specific date falls within any reassigned-away range
        const isReassignedAwayToday = (property.reassignedAwayRanges || []).some(
          range => date >= range.startDate && date <= range.endDate
        );

        // Find slot for this date + property combination
        const slot = isAssignedDay && !isReassignedAwayToday ? slots.find(
          (s) => new Date(s.date).toDateString() === dateString && s.propertyName === property.name
        ) : null;

        const isTempColumn = property.isTemporarilyReassignedTo && isInTempWindow;

        return (
          <td
            key={`${property._id}-${date.toISOString()}`}
            className={`relative whitespace-nowrap px-3 py-4 text-sm
              ${isToday(date) ? "bg-violet-950/40" : ""}
              ${(!isAssignedDay || isReassignedAwayToday) && !isTempColumn ? "bg-zinc-800/60" : ""}
              ${isTempColumn ? "bg-violet-950/20" : ""}
              ${propertyIndex < filteredProperties.length - 1 ? 'border-r border-zinc-800' : ''}
            `}
          >
            <div className="flex justify-center items-center">
              {slot && (
                <button
                  onClick={() => handleCheckmarkToggle(slot.reservationId)}
                  className="w-6 h-6 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110"
                  style={{
                    backgroundColor: checkedStates[slot.reservationId]
                      ? getCircleColor(slot.provider)
                      : "transparent",
                    border: `2px solid ${getCircleColor(slot.provider)}`,
                  }}
                >
                  {checkedStates[slot.reservationId] && (
                    <Check className="w-4 h-4 text-white" />
                  )}
                </button>
              )}
              {!isAssignedDay && !slot && !isTempColumn && (
                <div className="w-1.5 h-1.5 bg-zinc-700 rounded-full" />
              )}
            </div>
          </td>
        );
      });

      return (
        <tr
          key={date.toISOString()}
          className={`
            ${idx % 2 === 0 ? "bg-zinc-900" : "bg-zinc-950/50"}
            ${isSunday(date) ? "border-b border-zinc-700" : "border-b border-zinc-800"}
          `}
        >
          <td className={`whitespace-nowrap px-3 py-4 text-xs border-r border-zinc-800 text-center ${isToday(date) ? "text-violet-400 font-semibold" : "text-zinc-400"}`}>
            {formatDate(date)}
          </td>
          {cells}
        </tr>
      );
    });
  }, [calendarDates, filteredProperties, slots, checkedStates, handleCheckmarkToggle]);

  return (
    <div className="h-full flex flex-col bg-zinc-900 rounded-xl overflow-hidden">
      <div className="flex-none px-4 py-2 border-b border-zinc-800 flex justify-end items-center gap-4">
        <div className="text-xs text-zinc-500 flex items-center gap-1.5">
          <Clock className="w-3.5 h-3.5" />
          <span>Last sync: {formatSyncTime()}</span>
        </div>
        {!cleanerId && (
          <>
            <button
              onClick={() => setIsAllotmentsOpen(true)}
              className="inline-flex items-center px-3 py-1.5 text-xs font-medium bg-zinc-800 text-zinc-400 rounded-md hover:bg-zinc-700 transition-colors border border-zinc-700"
            >
              <CalendarDays className="w-3.5 h-3.5 mr-1.5" />
              Manage allotments
            </button>
            <button
              onClick={handleManualSync}
              disabled={isManualSyncing}
              className="inline-flex items-center px-3 py-1.5 text-xs font-medium bg-zinc-800 text-violet-400 rounded-md hover:bg-zinc-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors border border-zinc-700"
            >
              <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${isManualSyncing ? 'animate-spin' : ''}`} />
              {isManualSyncing ? 'Syncing...' : 'Sync Now'}
            </button>
          </>
        )}
      </div>

      <AllotmentsDrawer
        isOpen={isAllotmentsOpen}
        onClose={() => setIsAllotmentsOpen(false)}
        cleaners={cleaners}
        properties={properties}
      />

      <div className="flex-1 overflow-auto" ref={scrollRef}>
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-violet-500 mx-auto mb-4"></div>
              <p className="text-zinc-500 text-sm">Loading calendar...</p>
            </div>
          </div>
        ) : isInvalidLink ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <p className="text-zinc-500 text-base">Invalid calendar link.</p>
            </div>
          </div>
        ) : (
          <table className="min-w-full border-collapse">
            <thead className="bg-zinc-950">
              <tr>
                <th className="sticky top-0 z-10 bg-zinc-950 px-3 py-3 text-center text-xs font-medium text-zinc-500 uppercase tracking-wider border-r border-b border-zinc-800">
                  Date
                </th>
                {filteredProperties.map((property, index) => (
                  <th
                    key={property._id}
                    className={`sticky top-0 z-10 bg-zinc-950 px-3 py-3 text-center text-xs font-medium text-zinc-400 border-b border-zinc-800 ${
                      index < filteredProperties.length - 1 ? 'border-r border-zinc-800' : ''
                    }`}
                  >
                    <div>{property.name}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-zinc-900">
              {tableRows}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default Calendar;
